/**
 * @file: FeedThreadListView.tsx
 *
 * Feed activity-thread list — the "通知" surface. Navigation-agnostic
 * embeddable content (the inbox tab / desktop master column / the screen
 * wrapper mount it): the host passes `onOpenThread` (called with the raw
 * thread), an optional `isActive` predicate for the desktop master-detail
 * highlight, and `focused` (is this surface currently the visible one). The
 * view owns all feed list concerns: loading, pull-to-refresh, pagination, the
 * optimistic mark-read on tap, and "mark all read".
 *
 * Refresh policy: the data hook loads on mount; the view additionally
 * refreshes whenever `focused` flips false→true (tab switch / return from a
 * pushed detail). Computing focus is the host's job (it knows the navigator +
 * the active tab), so the view stays navigation-agnostic.
 *
 * Pinned section (system / maintenance-plans / rewards) is sticky on top;
 * the paginated others follow. The hook already filters server-side via
 * `scope` / `excludeScopes`, so the two arrays never overlap.
 */

import { ThreadRow } from '@euler/app/flows/feed/components/ThreadRow';
import { useFeedUnreadStore } from '@euler/app/flows/feed/lib/unread/context';
import {
  compareFeedThreadsForList,
  isFeedThreadPinned,
  isPinnedByScope,
  threadUsesManualRead,
} from '@euler/app/flows/feed/utils/feedThreadDescriptor';
import { useInboxListTheme } from '@euler/app/flows/inbox/lib/listTheme';
import { ListEmptyState } from '@euler/components/ListEmptyState';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useAlert } from '@euler/functions/ux/useAlert';
import { FeedThreadInboxItem } from '@euler/model/feed';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  Platform,
  RefreshControl,
  View,
} from 'react-native';
import { useFeedThreads } from '../functions/useFeedThreads';
import { SwipeableThreadRow } from './SwipeableThreadRow';
import { FeedThreadListSkeleton } from './skeleton/FeedThreadListSkeleton';

export const FeedThreadListView: FC<{
  /** Highlight predicate for the desktop master-detail column. */
  isActive?: (thread: FeedThreadInboxItem) => boolean;
  /** Whether this surface is the currently-visible one (drives re-focus refresh). */
  focused?: boolean;
  /**
   * Counter signal from the host — every time it changes the list silently
   * refreshes from the server. The host bumps it after issuing actions
   * whose effect needs to land in the list (e.g. the tab-bar's
   * "全部已读"). Silent because the user didn't pull — no pull indicator.
   */
  reloadTick?: number;
  onOpenThread: (thread: FeedThreadInboxItem) => void;
}> = memo(({ isActive, focused, reloadTick, onOpenThread }) => {
  const theme = useInboxListTheme();
  const { feedService } = useServiceFactory();
  const { feedUnreadCount$, refreshFeedUnread } = useFeedUnreadStore();
  const {
    pinnedThreads,
    otherThreads,
    loading,
    refreshing,
    hasMore,
    loadingMore,
    refresh,
    loadMore,
    patchThread,
    removeThread,
  } = useFeedThreads();
  const alert = useAlert();
  const { showActionSheetWithOptions } = useActionSheet();

  // Re-focus refresh: the hook owns the initial mount load, so skip the
  // first observed value and only refetch on a genuine false→true flip.
  const wasFocused = useRef(focused);
  useEffect(() => {
    if (focused && !wasFocused.current) void refresh({ silent: true });
    wasFocused.current = focused;
  }, [focused, refresh]);

  // Host-triggered silent reload (e.g. after the tab-bar mark-all-read).
  // Skip the initial value so a fresh mount doesn't fire a redundant load
  // on top of the hook's own mount-time refresh.
  const wasTick = useRef(reloadTick);
  useEffect(() => {
    if (reloadTick != null && reloadTick !== wasTick.current) {
      void refresh({ silent: true });
    }
    wasTick.current = reloadTick;
  }, [reloadTick, refresh]);

  // Scope-pinned essentials stay on top in their server order; the
  // paginated order threads sort pinned-first locally so an optimistic
  // pin/unpin re-ranks them before the next refresh confirms it.
  const threads = useMemo(
    () => [
      ...pinnedThreads,
      ...[...otherThreads].sort(compareFeedThreadsForList),
    ],
    [pinnedThreads, otherThreads],
  );

  const onThreadPress = useCallback(
    (thread: FeedThreadInboxItem) => {
      // Optimistic local: zero the unread count immediately so the burger
      // badge updates without waiting for the round-trip. Maintenance-plan
      // threads are exempt — their reminders stay unread until the user
      // acts on each one, so opening must not clear them.
      if (thread.unreadCount > 0 && !threadUsesManualRead(thread.scope)) {
        const delta = thread.unreadCount;
        patchThread(thread.id, { unreadCount: 0 });
        feedUnreadCount$.next(Math.max(0, feedUnreadCount$.getValue() - delta));
        feedService
          .markThreadRead(thread.id)
          .catch(onErrorIgnore)
          .finally(() => {
            void refreshFeedUnread();
          });
      }
      onOpenThread(thread);
    },
    [
      feedService,
      feedUnreadCount$,
      onOpenThread,
      patchThread,
      refreshFeedUnread,
    ],
  );

  // Pin/mute are optimistic: patch the row in place, fire the request, and
  // revert to the captured prior value if it fails.
  const onTogglePin = useCallback(
    (thread: FeedThreadInboxItem) => {
      const on = thread.pinnedAt == null;
      const prev = thread.pinnedAt ?? null;
      patchThread(thread.id, {
        pinnedAt: on ? new Date().toISOString() : null,
      });
      feedService
        .pinThread(thread.id, on)
        .catch(() => patchThread(thread.id, { pinnedAt: prev }));
    },
    [feedService, patchThread],
  );

  const onToggleMute = useCallback(
    (thread: FeedThreadInboxItem) => {
      const on = thread.mutedAt == null;
      const prev = thread.mutedAt ?? null;
      patchThread(thread.id, { mutedAt: on ? new Date().toISOString() : null });
      feedService
        .muteThread(thread.id, on)
        .catch(() => patchThread(thread.id, { mutedAt: prev }));
    },
    [feedService, patchThread],
  );

  // Archive is a reversible hide: the row leaves the default list (it lives
  // in the archived view until restored, or until new activity resurfaces
  // it). Archiving also marks the thread read on the server, so drop its
  // unread from the badge too; reconcile from the server only on failure.
  const onArchive = useCallback(
    (thread: FeedThreadInboxItem) => {
      const delta = thread.unreadCount;
      removeThread(thread.id);
      if (delta > 0) {
        feedUnreadCount$.next(Math.max(0, feedUnreadCount$.getValue() - delta));
      }
      feedService.archiveThread(thread.id, true).catch(() => {
        void refresh({ silent: true });
        void refreshFeedUnread();
      });
    },
    [feedService, removeThread, feedUnreadCount$, refresh, refreshFeedUnread],
  );

  // Delete is the hard, permanent removal: drop the row + its unread from the
  // badge, reconcile from the server only on failure.
  const onDelete = useCallback(
    (thread: FeedThreadInboxItem) => {
      const delta = thread.unreadCount;
      removeThread(thread.id);
      if (delta > 0) {
        feedUnreadCount$.next(Math.max(0, feedUnreadCount$.getValue() - delta));
      }
      feedService.deleteThread(thread.id).catch(() => {
        void refresh({ silent: true });
        void refreshFeedUnread();
      });
    },
    [feedService, removeThread, feedUnreadCount$, refresh, refreshFeedUnread],
  );

  // Confirm a destructive/irreversible-ish action before running it — a
  // native action sheet, or the web alert dialog. The confirm step is the
  // last gate: archive and delete both pass through here.
  const confirm = useCallback(
    (opts: {
      title: string;
      message: string;
      confirmLabel: string;
      destructive?: boolean;
      onConfirm: () => void;
    }) => {
      if (Platform.OS === 'web') {
        void alert({
          title: opts.title,
          message: opts.message,
          buttons: [
            { text: '取消', style: 'cancel' },
            {
              text: opts.confirmLabel,
              style: opts.destructive ? 'destructive' : undefined,
              onPress: opts.onConfirm,
            },
          ],
        });
        return;
      }
      showActionSheetWithOptions(
        {
          title: opts.title,
          message: opts.message,
          options: [opts.confirmLabel, '取消'],
          destructiveButtonIndex: opts.destructive ? 0 : undefined,
          cancelButtonIndex: 1,
        },
        i => {
          if (i === 0) opts.onConfirm();
        },
      );
    },
    [alert, showActionSheetWithOptions],
  );

  const confirmArchive = useCallback(
    (thread: FeedThreadInboxItem) =>
      confirm({
        title: '归档会话',
        message: '归档后会从通知列表移除，可在已归档中查看或恢复。',
        confirmLabel: '确认归档',
        onConfirm: () => onArchive(thread),
      }),
    [confirm, onArchive],
  );

  const confirmDelete = useCallback(
    (thread: FeedThreadInboxItem) =>
      confirm({
        title: '删除会话',
        message: '删除后该会话将从列表移除，新动态会重新创建。',
        confirmLabel: '确认删除',
        destructive: true,
        onConfirm: () => onDelete(thread),
      }),
    [confirm, onDelete],
  );

  // The "More" swipe action opens a sheet with the heavier, rarer actions
  // (archive / delete); each then asks for a final confirmation.
  const onMore = useCallback(
    (thread: FeedThreadInboxItem) => {
      if (Platform.OS === 'web') {
        void alert({
          title: '更多操作',
          buttons: [
            { text: '取消', style: 'cancel' },
            { text: '归档', onPress: () => confirmArchive(thread) },
            {
              text: '删除',
              style: 'destructive',
              onPress: () => confirmDelete(thread),
            },
          ],
        });
        return;
      }
      showActionSheetWithOptions(
        {
          options: ['归档', '删除', '取消'],
          destructiveButtonIndex: 1,
          cancelButtonIndex: 2,
        },
        i => {
          if (i === 0) confirmArchive(thread);
          else if (i === 1) confirmDelete(thread);
        },
      );
    },
    [alert, showActionSheetWithOptions, confirmArchive, confirmDelete],
  );

  const renderItem: ListRenderItem<FeedThreadInboxItem> = useCallback(
    ({ item, index }) => {
      // Edge-to-edge bottom hairline on the last pinned row marks the
      // pinned/unpinned section boundary (list is sorted pinned-first).
      const next = threads[index + 1];
      const isLastPinned =
        isFeedThreadPinned(item) && (next == null || !isFeedThreadPinned(next));
      // Only the regular order threads behave like a chat conversation
      // (pin / mute / more). The scope-pinned essentials (system / rewards /
      // maintenance-plans) are permanent surfaces — render them plain.
      if (isPinnedByScope(item.scope)) {
        return (
          <ThreadRow
            thread={item}
            active={isActive?.(item) ?? false}
            fullWidthDivider={isLastPinned}
            onPress={() => onThreadPress(item)}
          />
        );
      }
      return (
        <SwipeableThreadRow
          thread={item}
          active={isActive?.(item) ?? false}
          fullWidthDivider={isLastPinned}
          onPress={() => onThreadPress(item)}
          onTogglePin={onTogglePin}
          onToggleMute={onToggleMute}
          onMore={onMore}
        />
      );
    },
    [isActive, onThreadPress, threads, onTogglePin, onToggleMute, onMore],
  );

  if (loading && threads.length === 0) {
    return (
      <View style={{ flex: 1, backgroundColor: theme.panel }}>
        <FeedThreadListSkeleton />
      </View>
    );
  }

  if (threads.length === 0) {
    return <ListEmptyState title="暂无通知" subtitle="新消息会出现在这里" />;
  }

  return (
    // Panel bg comes from the inbox-list theme — desktop uses the WeChat-
    // style tinted gray that unpinned rows blend into; mobile is plain
    // white. Either way, only pinned / selected rows stand out.
    <View style={{ flex: 1, backgroundColor: theme.panel }}>
      <FlatList
        data={threads}
        keyExtractor={t => t.id}
        renderItem={renderItem}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={refresh} />
        }
        onEndReachedThreshold={0.5}
        onEndReached={hasMore ? loadMore : undefined}
        ListFooterComponent={<LoadMoreFooter loading={loadingMore} />}
        contentContainerStyle={{ flexGrow: 1 }}
      />
    </View>
  );
});

const LoadMoreFooter: FC<{ loading: boolean }> = memo(({ loading }) => {
  const { rpx } = useResponsiveContext();
  if (!loading) return null;
  return (
    <View
      css={`
        padding: ${rpx(16)}px 0;
      `}
    >
      <ActivityIndicator />
    </View>
  );
});
